import React, { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import TransactionTable from "./TransactionTable";
import styles from "./BankDataComponent.module.css";
import config from "../../config/config";
import Spinner from "../common/Spinner";

const BankAccountsData = ({
  setAccountsID,
  setIBAN,
  setExpandUrls,
  setMayGetExtraUrlForEachAccountNumberForExcel,
}) => {
  const navigate = useNavigate();

  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [transactionLoading, setTransactionLoading] = useState(false);
  const [expandedInstitution, setExpandedInstitution] = useState(null);
  const [expandedIBAN, setExpandedIBAN] = useState(null);
  const [expandedCurrency, setExpandedCurrency] = useState(null);
  const [transactions, setTransactions] = useState({});
  const [searchTransaction, setSearchTransaction] = useState("");
  const [errorMessage, setErrorMessage] = useState(null);
  const [excelInstructions, setExcelInstructions] = useState(false);
  const [redirectUrl, setRedirectUrl] = useState("");
  const [message, setMessage] = useState(null);
  const [messagePositive, setMessagePositive] = useState(null);
  const [expandedTransaction, setExpandedTransaction] = useState(null);
  const [sortOrder, setSortOrder] = useState("desc"); // Default to descending order
  const [isAnyCardExpanded, setIsAnyCardExpanded] = useState(false);
  const [
    ibanAlreadyFetchedAllTransactions,
    setIbanAlreadyFetchedAllTransactions,
  ] = useState([]);

  const containerRef = useRef(null);

  const [isAtStart, setIsAtStart] = useState(true);
  const [isAtEnd, setIsAtEnd] = useState(false);

  const [transactionPage, setTransactionPage] = useState(1); // Track the current page
  const [hasMoreTransactions, setHasMoreTransactions] = useState(true); // Track if more transactions are available
  const [isFetching, setIsFetching] = useState(false); // Add a fetching flag
  const [allTransactionsFetched, setAllTransactionsFetched] = useState(false); // Track if all transactions are fetched

  const handleScroll = () => {
    if (containerRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = containerRef.current;
      setIsAtStart(false); // Always keep the left arrow active
      setIsAtEnd(false); // Always keep the right arrow active
    }
  };

  useEffect(() => {
    if (containerRef.current) {
      containerRef.current.addEventListener("scroll", handleScroll);
      handleScroll(); // Initialize state on mount
      return () =>
        containerRef.current.removeEventListener("scroll", handleScroll);
    }
  }, []);

  const scrollLeft = () => {
    if (containerRef.current) {
      const scrollAmount = containerRef.current.offsetWidth / 2; // Scroll by half the container width
      containerRef.current.scrollBy({
        left: -scrollAmount,
        behavior: "smooth",
      });
      handleScroll(); // Update arrow states after scrolling
    }
  };

  const scrollRight = () => {
    if (containerRef.current) {
      const scrollAmount = containerRef.current.offsetWidth / 2; // Scroll by half the container width
      containerRef.current.scrollBy({
        left: scrollAmount,
        behavior: "smooth",
      });
      handleScroll(); // Update arrow states after scrolling
    }
  };

  useEffect(() => {
    if (containerRef.current) {
      containerRef.current.scrollTo({
        left: 0, // Ensure the first card is visible on load
        behavior: "auto",
      });
    }
  }, []);

  const handleExcelInstructionsClick = () => {
    setExcelInstructions(!excelInstructions);
  };

  const fetchAllTransactionsForSearch = async (
    iban,
    currency,
    sort = "desc"
  ) => {
    if (isFetching || allTransactionsFetched) {
      console.log(
        "Fetch already in progress or all transactions already fetched, skipping..."
      );
      return; // Prevent duplicate fetches
    }

    setIsFetching(true); // Set fetching flag to true
    // setTransactionLoading(true);

    try {
      const findAccount = accounts.find((account) => account.iban === iban);
      const accountId = findAccount.id;
      const response = await fetch(
        `${config.URL_PROD}/api/v1/bank/transactionsAsked/${accountId}?all=true&sort=${sort}`,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error(
          `Failed to fetch all transactions: ${response.statusText}`
        );
      }

      const result = await response.json();
      const allTransactions = result.transactionsDataWithMerchant.map((t) => ({
        ...t,
        sort, // Add sort information to each transaction
      }));

      console.log("Fetched all transactions for search:", allTransactions);
      setTransactions((prevTransactions) => ({
        ...prevTransactions,
        [iban]: allTransactions,
      }));
      setAllTransactionsFetched(true); // Mark all transactions as fetched
      setIbanAlreadyFetchedAllTransactions((prev) => [...prev, iban]); // Track IBANs that have all transactions fetched
    } catch (error) {
      console.error(
        "Error fetching all transactions for search:",
        error.message
      );
    } finally {
      setTransactionLoading(false);
      setIsFetching(false); // Reset fetching flag
    }
  };

  const handleSearchTransaction = (value) => {
    setSearchTransaction(value); // Update the searchTransaction state

    if (
      value.trim() &&
      expandedIBAN &&
      expandedCurrency &&
      !allTransactionsFetched
    ) {
      // Fetch all transactions for the current IBAN and currency if search input is not empty
      fetchAllTransactionsForSearch(expandedIBAN, expandedCurrency, sortOrder);
    }
  };

  const toggleInstitution = (institutionId) => {
    setExpandedTransaction(null);
    setSortOrder("desc"); // Reset Sort by Date to "Newest First"
    setSearchTransaction(""); // Reset search input when switching banks
    setExpandedIBAN(null); // Reset selected IBAN
    setExpandedCurrency(null); // Reset selected currency
    setExpandedInstitution((prev) =>
      prev === institutionId ? null : institutionId
    );
  };

  const fetchTransactions = async (iban, currency, page = 1, sort = "desc") => {
    // check if the transactions for this iban already exist
    if (ibanAlreadyFetchedAllTransactions.length > 0) {
      const alreadyFetched = ibanAlreadyFetchedAllTransactions.some(
        (fetchedIban) => fetchedIban === iban
      );
      if (alreadyFetched) {
        console.log(
          `Transactions for IBAN ${iban} already fetched all transactions.`
        );
        return; // Skip fetching if all transactions are already fetched
      }
    }

    if (isFetching || allTransactionsFetched) {
      console.log(
        "Fetch already in progress or all transactions already fetched, skipping..."
      );
      return; // Prevent duplicate fetches or fetching additional pages
    }

    setIsFetching(true); // Set fetching flag to true
    const limit = page === 1 ? 50 : 30;

    if (transactions[iban]?.some((t) => t.page === page && t.sort === sort)) {
      console.log(
        `Transactions for page ${page} with sort ${sort} already fetched.`
      );
      setIsFetching(false); // Reset fetching flag
      return; // Avoid fetching the same page again
    }

    setTransactionLoading(true);
    try {
      const findAccount = accounts.find((account) => account.iban === iban);
      const accountId = findAccount.id;
      const response = await fetch(
        `${config.URL_PROD}/api/v1/bank/transactionsAsked/${accountId}?page=${page}&limit=${limit}&sort=${sort}`,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error(`Failed to fetch transactions: ${response.statusText}`);
      }

      const result = await response.json();
      const newTransactions = result.transactionsDataWithMerchant.map((t) => ({
        ...t,
        page, // Add page information to each transaction
        sort, // Add sort information to each transaction
      }));

      console.log("Fetched transactions:", newTransactions);
      setTransactions((prevTransactions) => ({
        ...prevTransactions,
        [iban]: [...(prevTransactions[iban] || []), ...newTransactions],
      }));

      setHasMoreTransactions(newTransactions.length === limit); // Check if more transactions are available
      if (newTransactions.length < limit) {
        setAllTransactionsFetched(true); // Mark all transactions as fetched if fewer than the limit are returned
      }
    } catch (error) {
      console.error("Error fetching transactions:", error.message);
    } finally {
      setTransactionLoading(false);
      setIsFetching(false); // Reset fetching flag
    }
  };

  useEffect(() => {
    // Fetch transactions only if expandedIBAN, expandedCurrency, and transactionPage are valid
    if (expandedIBAN && expandedCurrency && transactionPage > 0) {
      console.log(`Fetching transactions for page ${transactionPage}`);
      fetchTransactions(
        expandedIBAN,
        expandedCurrency,
        transactionPage,
        sortOrder
      );
    }
  }, [expandedIBAN, expandedCurrency, transactionPage, sortOrder]);

  const toggleIBAN = async (iban, currency) => {
    setSearchTransaction(""); // Reset search input when switching IBANs
    setSortOrder("desc"); // Reset Sort by Date to "Newest First"

    // Collapse if the same account and currency are clicked again
    if (expandedIBAN === iban && expandedCurrency === currency) {
      setExpandedIBAN(null);
      setExpandedCurrency(null);
      setIsAnyCardExpanded(false); // No card is expanded
      return;
    }

    // Update the expanded IBAN and Currency
    setExpandedIBAN(iban);
    setExpandedCurrency(currency);
    setIsAnyCardExpanded(true); // A card is expanded

    // Check if transactions for this IBAN already exist
    if (transactions[iban] && transactions[iban].length > 0) {
      console.log(
        `Transactions for IBAN ${iban} already exist, skipping fetch.`
      );
      return; // Skip fetching if transactions already exist
    }

    // Reset fetching state for the new IBAN
    setTransactionPage(1); // Reset page when switching IBANs
    setHasMoreTransactions(true); // Reset hasMoreTransactions
    setAllTransactionsFetched(false); // Reset allTransactionsFetched for the new IBAN

    fetchTransactions(iban, currency, 1, sortOrder); // Fetch initial transactions
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await fetch(
          `${config.URL_PROD}/api/v1/bank/getAccountsAndBalances`,
          {
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
          }
        );

        if (!response.ok) {
          if (response.status === 302) {
            const result = await response.json();
            setErrorMessage(result.message);
            setRedirectUrl(result.redirect);
            return;
          }
        }
        if (!response.ok) {
          throw new Error(`Failed to fetch data: ${response.statusText}`);
        }

        const result = await response.json();

        if (
          result.message &&
          result.message?.startsWith("Your requisitions have expired.")
        ) {
          return setErrorMessage(result.message);
        }
        if (result.message?.startsWith("Failed to fetch account")) {
          return setErrorMessage(result.message);
        }

        if (result.message === "No accounts with balances found") {
          setErrorMessage(
            "Sorry but we are working on the issue and will be back soon"
          );
          const timer = setTimeout(() => {
            setErrorMessage(null);
          }, 1500);
          return setAccounts([]);
        }

        setAccounts(result);
        localStorage.setItem("accounts", JSON.stringify(result));
        setAccountsID(result.map((account) => account.id));
        // add accounts id to location
        setIBAN(result.map((account) => account.iban));
        const url = result.map((account) => account.iban);
        setExpandUrls(url);
        setMayGetExtraUrlForEachAccountNumberForExcel(
          result.length > 1 ? true : false
        );
      } catch (error) {
        console.error("Error fetching data:", error.message);
        if (error.message === "Failed to fetch data: Unauthorized") {
          navigate("/Login");
        } else {
          setErrorMessage(error.message);
        }
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [navigate, setAccountsID]);

  useEffect(() => {
    if (errorMessage) {
      const timer = setTimeout(() => {
        setErrorMessage(null);
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [errorMessage]);

  const onScrollToBottom = (scrollTop, scrollHeight, clientHeight) => {
    if (allTransactionsFetched) {
      console.log(
        "All transactions are already fetched, skipping further fetches."
      );
      return; // Prevent additional fetches if all transactions are already fetched
    }

    const threshold = 30; // Number of transactions to scroll before fetching the next page
    const totalLoadedTransactions = transactions[expandedIBAN]?.length || 0;
    const estimatedTransactionHeight = scrollHeight / totalLoadedTransactions;

    const scrollThreshold =
      totalLoadedTransactions * estimatedTransactionHeight - clientHeight;

    if (
      scrollTop >= scrollThreshold &&
      hasMoreTransactions &&
      !transactionLoading &&
      !isFetching // Ensure no fetch is already in progress
    ) {
      const nextPage = transactionPage + 1;
      console.log(`Fetching next page: ${nextPage}`);
      fetchTransactions(expandedIBAN, expandedCurrency, nextPage, sortOrder); // Pass sortOrder to fetchTransactions
      setTransactionPage(nextPage); // Increment the page
    }
  };

  if (loading) {
    return <Spinner />;
  }

  if (errorMessage) {
    return (
      <div className={styles.errorMessage}>
        <button
          className={styles.errorCloseBtn}
          onClick={() => setErrorMessage(null)}
        >
          X
        </button>
        <p>{errorMessage}</p>{" "}
        {redirectUrl && (
          <button
            className={styles.reconnectBtn}
            onClick={() => (window.location.href = redirectUrl)}
          >
            Reconnect
          </button>
        )}
      </div>
    );
  }

  const groupedAccounts = accounts?.reduce((acc, account) => {
    if (!acc[account.institution_id]) {
      acc[account.institution_id] = [];
    }
    acc[account.institution_id].push(account);
    return acc;
  }, {});

  const MayGetExtraUrlForEachAccountNumberForExcel =
    localStorage.getItem("getExtraUrlForEachAccountNumberForExcel") === "true";

  const handleAddAccountClick = () => {
    setMessage(
      "You have reached the limit. Contact support to increase the limit."
    );
    setTimeout(() => {
      setMessage(null);
    }, 4000);
  };

  const handlePositiveMessageOnClick = (message) => {
    setMessagePositive(message);
    setTimeout(() => {
      setMessagePositive(null);
    }, 2000);
  };

  const getCurrencySymbol = (currency) => {
    if (!currency) {
      return "€"; // If currency is not available, return as euro
    }

    try {
      return new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: currency,
      })
        .formatToParts(0)
        .find((part) => part.type === "currency").value;
    } catch (e) {
      return "€"; // If an error occurs, return as euro
    }
  };

  const truncateIBAN = (iban, maxLength = 16) => {
    if (iban.length > maxLength) {
      return iban.substring(0, maxLength) + "**";
    }
    return iban;
  };

  return (
    <div className={`${styles.main}`}>
      <div
        className={`${styles.navigationWrapper} ${
          !isAnyCardExpanded
            ? styles.expandedContainer
            : styles.defaultContainer
        }`}
      >
        <button className={styles.scrollButton} onClick={scrollLeft}>
          &#8592;
        </button>
        <div
          className={`${styles.bankAccountsContainer} ${
            !isAnyCardExpanded ? styles.expanded : styles.default
          }`}
          ref={containerRef}
        >
          {accounts.map((account) => {
            const isExpanded =
              expandedIBAN === account.iban &&
              expandedCurrency ===
                (account?.balanceAmount?.currency || account?.currency);

            return (
              <div
                key={account.id}
                className={`${styles.accountCard} ${
                  !isAnyCardExpanded
                    ? styles.defaultCard
                    : isExpanded
                    ? styles.expandedCard
                    : styles.dimmedCard
                }`}
                onClick={() =>
                  toggleIBAN(
                    account.iban,
                    account?.balanceAmount?.currency || account?.currency
                  )
                }
              >
                <div
                  className={`${styles.accountHeader} ${
                    !isAnyCardExpanded ? styles.default : ""
                  }`}
                >
                  <div
                    className={`${
                      !isAnyCardExpanded
                        ? styles.ibanLogoDefault
                        : styles.ibanLogo
                    }`}
                  >
                    <img
                      className={styles.institutionLogo}
                      src={account.bankLogo || "default-logo.png"} // Use a default logo if none is provided
                      alt={`${account.bankName || "Institution"} Logo`}
                    />
                    <p
                      className={`${
                        !isAnyCardExpanded ? styles.default : styles.accountIban
                      } `}
                    >
                      {truncateIBAN(account.iban)}
                    </p>
                  </div>
                  <p className={styles.accountBalance}>
                    <strong>Balance:</strong>{" "}
                    {getCurrencySymbol(
                      account?.currency || account.balanceAmount?.currency
                    ) +
                      (isNaN(
                        Number(
                          account.balanceAmount?.amount ||
                            account?.balanceAmount
                        )
                      )
                        ? "❔"
                        : Number(
                            account.balanceAmount?.amount ||
                              account?.balanceAmount
                          ))}
                  </p>
                </div>
              </div>
            );
          })}
          {/* Add Account Card */}
          <div
            className={`${
              !isAnyCardExpanded
                ? styles.accountCardDefault
                : styles.accountCard
            } ${expandedIBAN ? styles.dimmedCard : ""}`}
            onClick={() => navigate("/ConnectToBank")} // Navigate to Add Account page
          >
            <div className={styles.accountHeader}>
              <p
                className={
                  !isAnyCardExpanded
                    ? styles.merchantNameDefault
                    : styles.merchantName
                }
              >
                + Add Account
              </p>
            </div>
          </div>
        </div>
        <button className={styles.scrollButton} onClick={scrollRight}>
          &#8594;
        </button>
      </div>
      {expandedIBAN && (
        <div className={styles.transactionsRow}>
          <TransactionTable
            transactions={transactions[expandedIBAN]?.filter(
              (transaction, index, self) =>
                transaction.transactionAmount?.currency === expandedCurrency &&
                self.findIndex((t) => t._id === transaction._id) === index && // Filter out duplicates
                transaction._id // Filter transactions by _id
            )}
            account={accounts.find((account) => account.iban === expandedIBAN)}
            handleSearchTransaction={searchTransaction}
            setSearchTransaction={handleSearchTransaction} // Pass the updated handler
            sortOrder={sortOrder}
            setSortOrder={setSortOrder}
            loading={transactionLoading} // Pass transactionLoading as the loading prop
            onScrollToBottom={onScrollToBottom} // Notify parent when scrolled to bottom
          />
        </div>
      )}
    </div>
  );
};

export default BankAccountsData;
